"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { Menu } from "lucide-react";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export function MyNav({
  className = "",
  invisibleDiv = false,
  adminHeader = false,
}) {
  const router = useRouter();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  return (
    <div
      className={`flex items-center justify-between! sm:justify-start w-full ${className}`}
    >
      {/* Logo */}
      <Link href="/">
        <Image
          src="/logo/payreless-sidebar.png"
          alt="payreless"
          unoptimized
          width={100}
          height={30}
          className="h-auto object-contain -mt-[4px] mr-6"
        />
      </Link>

      {/* Desktop Navigation */}
      <NavigationMenu className={`hidden sm:flex items-center gap-6`}>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuLink
              asChild
              className="bg-transparent text-primary-foreground font-medium hover:bg-primary hover:text-primary-foreground"
            >
              <Link href="/">Home</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuTrigger className="bg-transparent text-primary-foreground px-2 hover:bg-primary hover:text-primary-foreground">
              Templates
            </NavigationMenuTrigger>
            <NavigationMenuContent className="md:w-[300px] lg:w-[400px]">
              <ul className="grid gap-2 text-primary-foreground">
                <ListItem
                  href="/templates?category=hotspot"
                  title="Hotspot"
                  className="w-full text-foreground"
                >
                  Router hotspot login templates for your customer
                </ListItem>
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuLink
              asChild
              className="bg-transparent text-primary-foreground font-medium hover:bg-primary hover:text-primary-foreground"
            >
              <Link href="/about">About</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>

          <NavigationMenuItem>
            <NavigationMenuLink
              asChild
              className="bg-transparent text-primary-foreground font-medium hover:bg-primary hover:text-primary-foreground"
            >
              <Link href="#contact">Contact</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>

          {invisibleDiv && adminHeader && (
            <NavigationMenuItem>
              <Button variant="secondary" onClick={() => router.push("/dash")}>
                Dashboard
              </Button>
            </NavigationMenuItem>
          )}
        </NavigationMenuList>
      </NavigationMenu>

      {/* Mobile Navigation (Hamburger) */}
      <div className="sm:hidden">
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetHeader className="hidden">
            <SheetTitle />
          </SheetHeader>
          <SheetContent side="right" className="w-64">
            <nav className="px-4 mt-8 flex flex-col gap-4">
              <Link href="/" onClick={() => setIsMobileMenuOpen(false)}>
                Home
              </Link>
              <Link
                href="/templates?category=hotspot"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Templates
              </Link>
              <Link href="/about" onClick={() => setIsMobileMenuOpen(false)}>
                About
              </Link>
              <Link href="#contact" onClick={() => setIsMobileMenuOpen(false)}>
                Contact
              </Link>
              {invisibleDiv && adminHeader && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    router.push("/dash");
                  }}
                >
                  Dashboard
                </Button>
              )}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

function ListItem({
  title,
  children,
  href,
  ...props
}: React.ComponentPropsWithoutRef<"li"> & { href: string }) {
  return (
    <li {...props}>
      <NavigationMenuLink asChild>
        <Link href={href}>
          <div className="text-sm leading-none font-medium">{title}</div>
          <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}
